/**
 * Browser-side error reporter.
 *
 * Posts error details to the `client-errors` Lambda Function URL (CloudWatch
 * Logs destination in eu-west-1). Fails silently — an error inside the
 * reporter must never crash the app or shadow the original error.
 *
 * Fed from three sources:
 *   - React ErrorBoundary (componentDidCatch)
 *   - window.onerror (uncaught sync/async errors)
 *   - window.onunhandledrejection (unhandled promise rejections)
 *
 * Payload fields allow-listed on the server; anything else is dropped.
 */

type ClientErrorKind = 'react' | 'window.onerror' | 'unhandledrejection';

interface ReportArgs {
  kind: ClientErrorKind;
  error: unknown;
  componentStack?: string | null;
  userId?: string | null;
}

// Build-time env var — set to the Function URL from the 09-client-errors stack.
// Leave empty in local dev to disable reporting entirely.
const ENDPOINT = import.meta.env.VITE_CLIENT_ERRORS_URL as string | undefined;
const RELEASE = import.meta.env.VITE_APP_VERSION as string | undefined;

// Simple in-memory throttle: cap at ~1 report every 2s per tab to avoid loops.
let lastReportAt = 0;
const MIN_INTERVAL_MS = 2_000;

export function reportError({ kind, error, componentStack, userId }: ReportArgs): void {
  if (!ENDPOINT) return;

  const now = Date.now();
  if (now - lastReportAt < MIN_INTERVAL_MS) return;
  lastReportAt = now;

  const err = error instanceof Error ? error : new Error(String(error));
  const body = {
    errorType: kind,
    message: err.message?.slice(0, 1_000),
    stack: err.stack?.slice(0, 4_000),
    componentStack: componentStack?.slice(0, 4_000) ?? null,
    url: typeof location !== 'undefined' ? location.href : null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    userId: userId ?? null,
    release: RELEASE ?? null,
  };

  // sendBeacon is preferred — survives page unload and never blocks the
  // renderer. Falls back to fetch(keepalive) for browsers without it.
  try {
    const payload = JSON.stringify(body);
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: payload,
      keepalive: true,
      credentials: 'omit',
      mode: 'cors',
    }).catch(() => {
      /* swallow — never crash on our own crash reporter */
    });
  } catch {
    /* swallow — never crash on our own crash reporter */
  }
}

/**
 * Install global handlers for uncaught errors and unhandled promise rejections.
 * Call once at app boot. Idempotent.
 */
let installed = false;
export function installGlobalErrorHandlers(getUserId?: () => string | null): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    reportError({
      kind: 'window.onerror',
      error: event.error ?? event.message,
      userId: getUserId?.() ?? null,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportError({
      kind: 'unhandledrejection',
      error: event.reason,
      userId: getUserId?.() ?? null,
    });
  });
}
